import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
// TODO: If Runium uses custom JWT auth instead of Clerk, replace useAuth with custom hook
import { useAuth, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getApiUrl } from '../../utils/apiUtils';
import { useEvaluationModal } from './hooks/useEvaluationModal';
import { formatEvaluationForCopy } from './utils';
import { ConversationHistoryRenderer } from './components/ConversationHistoryRenderer';
import EvaluationHeader from './components/EvaluationHeader';
import EvaluationScores from './components/EvaluationScores';
import ConversationHistory from './components/ConversationHistory';
import CoachResponse from './components/CoachResponse';
import JudgeFeedback from './components/JudgeFeedback';
import TechnicalDetails from './components/TechnicalDetails';
import ContextSections from './components/ContextSections';
import { SECTION_KEYS } from './constants';
import ErrorBoundary from './ErrorBoundary';

// Admin whitelist check (matches AdminFeedbackPage pattern)
const RAW_ALLOWLIST = (process.env.REACT_APP_ADMIN_EMAIL_WHITELIST || '')
  .split(',')
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);

const ADMIN_EMAILS = RAW_ALLOWLIST.filter((entry) => entry.includes('@'));
const ADMIN_IDS = RAW_ALLOWLIST.filter((entry) => !entry.includes('@'));

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]; // Reasonable increments, max 100 to avoid overload

// Default evaluation suites - fallback if API fails
// Note: Removed invalid suite IDs - will load from API instead
const DEFAULT_SUITES = [
  // Suites will be loaded from API - no hardcoded defaults
];

const ScoreBadge = ({ score }) => {
  if (score === null || score === undefined) {
    return (
      <span className="inline-flex items-center rounded-full border border-gray-600 bg-gray-800/30 px-2 py-0.5 text-xs font-medium text-gray-400">
        N/A
      </span>
    );
  }
  const percentage = (score * 100).toFixed(1);
  const getColor = () => {
    if (score >= 0.8) return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40';
    if (score >= 0.6) return 'bg-amber-500/15 text-amber-200 border-amber-500/40';
    return 'bg-red-500/15 text-red-200 border-red-500/40';
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getColor()}`}>
      {percentage}%
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const colors = {
    equipment: 'bg-blue-500/15 text-blue-200 border-blue-500/40',
    nutrition_advice: 'bg-purple-500/15 text-purple-200 border-purple-500/40',
    training: 'bg-green-500/15 text-green-200 border-green-500/40',
    injury_prevention: 'bg-orange-500/15 text-orange-200 border-orange-500/40',
    safety: 'bg-yellow-500/15 text-yellow-200 border-yellow-500/40',
  };
  const color = colors[category] || 'bg-gray-500/15 text-gray-300 border-gray-500/40';
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs capitalize ${color}`}>
      {category?.replace('_', ' ') || 'Unknown'}
    </span>
  );
};

const MetricsCard = ({ label, value, subtitle }) => (
  <div className="rounded-xl border border-gray-800 bg-black/20 p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl font-semibold text-white mt-1">{value}</p>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

const ScoreDistributionBar = ({ label, count, total, percentage, onClick, isActive }) => {
  const barWidth = total > 0 ? (count / total) * 100 : 0;
  const safePercentage = percentage ?? 0;
  const getBarColor = () => {
    if (safePercentage >= 80) return 'bg-emerald-400';
    if (safePercentage >= 60) return 'bg-amber-400';
    return 'bg-red-400';
  };
  
  return (
    <div 
      className={`space-y-1 cursor-pointer transition-all duration-200 rounded-lg p-2 -m-2 ${
        isActive 
          ? 'bg-blue-500/20 border border-blue-500/50' 
          : 'hover:bg-gray-800/50 border border-transparent'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-center justify-between text-sm">
        <span className={`${isActive ? 'text-blue-200 font-medium' : 'text-gray-300'}`}>{label}</span>
        <span className={`font-semibold ${isActive ? 'text-blue-200' : 'text-white'}`}>
          {count} ({safePercentage.toFixed(1)}%)
        </span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div 
          className={`${getBarColor()} h-2 rounded-full transition-all duration-300 ${
            isActive ? 'ring-2 ring-blue-400 ring-offset-1 ring-offset-gray-900' : ''
          }`} 
          style={{ width: `${barWidth}%` }} 
        />
      </div>
    </div>
  );
};

const DetailModal = ({ result, onClose, onPrevious, onNext, hasPrevious, hasNext }) => {
  // Use custom hook for modal state management
  const {
    modalRef,
    expandedCoachStates,
    expandedJudgeStates,
    allExpandedCoach,
    allExpandedJudge,
    toggleCoachSection,
    toggleJudgeSection,
    toggleAllCoach,
    toggleAllJudge,
  } = useEvaluationModal(result, onClose, onPrevious, onNext, hasPrevious, hasNext);

  // Memoize copy text for full evaluation
  const score = result?.evaluation?.score ?? 0;
  const copyText = useMemo(() => {
    if (!result) return '';
    return formatEvaluationForCopy(result, score);
  }, [result, score]);

  const handleCopyFull = useCallback(async (text) => {
    // Validate input
    if (!text) {
      console.warn('No content to copy');
      alert('No content available to copy.');
      return;
    }

    // Ensure text is a string
    const textToCopy = String(text);
    
    if (!textToCopy || textToCopy.trim().length === 0) {
      console.warn('Empty content to copy');
      alert('No content available to copy.');
      return;
    }

    // Try modern clipboard API first (if available)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        return; // Success
      } catch (err) {
        // Clipboard API failed, fall through to fallback
        console.debug('Clipboard API failed, using fallback:', err);
      }
    }
    
    // Fallback: use execCommand for older browsers or when clipboard API fails
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.style.opacity = '0';
      textArea.style.zIndex = '-9999';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      // For iOS
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      }
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed:', fallbackErr);
      // Last resort: create a temporary textarea that user can manually copy from
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.top = '50%';
      textArea.style.left = '50%';
      textArea.style.transform = 'translate(-50%, -50%)';
      textArea.style.width = '80%';
      textArea.style.height = '60%';
      textArea.style.padding = '1rem';
      textArea.style.border = '2px solid #3b82f6';
      textArea.style.borderRadius = '0.5rem';
      textArea.style.backgroundColor = '#1f2937';
      textArea.style.color = '#f3f4f6';
      textArea.style.zIndex = '9999';
      textArea.style.fontSize = '0.875rem';
      textArea.style.fontFamily = 'monospace';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      alert('Please copy the text from the text area that appeared, then close it.');
      
      // Auto-remove after 30 seconds or when user clicks outside
      const removeTextArea = () => {
        if (document.body.contains(textArea)) {
          document.body.removeChild(textArea);
        }
      };
      
      setTimeout(removeTextArea, 30000);
      textArea.addEventListener('blur', removeTextArea, { once: true });
    }
  }, []);

  // Render conversation history helper
  const renderConversationHistory = useCallback(() => {
    return <ConversationHistoryRenderer history={result?.history} respondedToIndex={result?.responded_to_index} />;
  }, [result?.history, result?.responded_to_index]);

  if (!result) return null;
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="evaluation-modal-title"
      aria-describedby="evaluation-modal-description"
    >
      <div 
        ref={modalRef}
        className="bg-gray-900 rounded-2xl border border-gray-800 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <EvaluationHeader
          caseIndex={result.case_index}
          onClose={onClose}
          onPrevious={onPrevious}
          onNext={onNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
        <div className="overflow-y-auto px-6 py-4 space-y-6">
          {/* Zone 1: Header & Quick Summary */}
          <EvaluationScores
            result={result}
            score={score}
            CategoryBadge={CategoryBadge}
            ScoreBadge={ScoreBadge}
            onCopyFull={handleCopyFull}
          />

          {/* Zone 2: Core Evaluation Flow - Conversation History → Coach Response → Judge Feedback */}
          <div className="space-y-4 border-t border-gray-800 pt-6">
            {/* Conversation History */}
            {result.history && Array.isArray(result.history) && result.history.length > 0 && (
              <ConversationHistory
                history={result.history}
                respondedToIndex={result.responded_to_index}
                expanded={expandedCoachStates[SECTION_KEYS.COACH.HISTORY]}
                onToggle={(newState) => toggleCoachSection(SECTION_KEYS.COACH.HISTORY, newState)}
              />
            )}

            {/* Coach Response */}
            <CoachResponse
              coachResponse={result.coach_response}
              expanded={true}
            />

            {/* Judge Feedback */}
            <JudgeFeedback
              evaluation={result.evaluation}
              expanded={true}
            />
          </div>

          {/* Zone 4 & 5: Context Sections */}
          <ContextSections
            result={result}
            expandedCoachStates={expandedCoachStates}
            expandedJudgeStates={expandedJudgeStates}
            allExpandedCoach={allExpandedCoach}
            allExpandedJudge={allExpandedJudge}
            toggleCoachSection={toggleCoachSection}
            toggleJudgeSection={toggleJudgeSection}
            toggleAllCoach={toggleAllCoach}
            toggleAllJudge={toggleAllJudge}
            renderConversationHistory={renderConversationHistory}
          />

          {/* Zone 6: Technical Metadata */}
          <TechnicalDetails result={result} />

        </div>
      </div>
    </div>
  );
};

DetailModal.propTypes = {
  result: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  hasPrevious: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool.isRequired,
};

const EvaluationDashboard = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const primaryEmail = user?.primaryEmailAddress?.emailAddress?.toLowerCase();
  const clerkId = user?.id?.toLowerCase();

  // Admin whitelist check
  const isConfigured = RAW_ALLOWLIST.length > 0;
  const isAuthorized = useMemo(() => {
    if (!isConfigured || !(primaryEmail || clerkId)) {
      return false;
    }
    return ADMIN_EMAILS.includes(primaryEmail) || ADMIN_IDS.includes(clerkId);
  }, [primaryEmail, clerkId, isConfigured]);

  const [selectedSuiteId, setSelectedSuiteId] = useState(null); // Will be set from API
  const [availableSuites, setAvailableSuites] = useState(DEFAULT_SUITES);
  const [evaluationData, setEvaluationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [scoreFilter, setScoreFilter] = useState(null); // 'high', 'medium', 'low', or null
  const [filters, setFilters] = useState({
    category: '',
    expected_quality: '',
    minScore: 0,
    maxScore: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({ key: 'case_index', direction: 'asc' });
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [judgeRubric, setJudgeRubric] = useState(null);
  const [rubricLoading, setRubricLoading] = useState(false);
  const [showRubric, setShowRubric] = useState(false);
  const rowRefs = useRef(new Map());

  const loadEvaluationData = useCallback(async (suiteId) => {
    setLoading(true);
    setError('');
    setCurrentPage(1);

    try {
      // Try API endpoint first
      const apiUrl = getApiUrl();
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let response = await fetch(`${apiUrl}/api/evaluation/suite/${suiteId}`, { headers });
      
      // Fallback to static file if API fails
      if (!response.ok) {
        console.warn(`API endpoint failed (${response.status}), falling back to static file`);
        try {
          const staticResponse = await fetch(`/evaluation_results_${suiteId}.json`);
          if (staticResponse.ok) {
            const contentType = staticResponse.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              const data = await staticResponse.json();
              setEvaluationData(data);
              setLastFetchedAt(new Date().toISOString());
              return;
            }
          }
        } catch (staticError) {
          // Ignore static file errors, continue with main error
        }
        // If we get here, both API and static file failed
        const errorText = await response.text();
        console.error('[EvaluationDashboard] Results API error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || `Suite "${suiteId}" not found. Available suites: ${availableSuites.map(s => s.name).join(', ') || 'none'}`}`);
      }
      
      // Check if response is actually JSON before parsing
      const contentType = response.headers.get('content-type') || '';
      let backendData;
      
      if (contentType.includes('application/json')) {
        // It's JSON, parse it
        backendData = await response.json();
      } else {
        // Not JSON, read as text to see what we got
        const text = await response.text();
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw new Error(`Backend returned HTML instead of JSON. The API endpoint may not exist or there's a routing issue. Status: ${response.status}. URL: ${apiUrl}/api/evaluation/suite/${suiteId}`);
        }
        // Try to parse as JSON anyway (some APIs don't set content-type correctly)
        try {
          backendData = JSON.parse(text);
        } catch (parseError) {
          throw new Error(`Expected JSON response but got ${contentType || 'unknown'}. Response preview: ${text.substring(0, 200)}`);
        }
      }
      
      // Transform backend response to match dashboard expected format
      const suite = backendData.suite || backendData; // Handle both formats
      
      // Transform cases - handle both old format (flat) and new format (nested evaluation)
      const transformedResults = (suite.data || []).map((caseItem, index) => {
        // Extract case_index from id (e.g., "case_0" -> 1, "case_1" -> 2)
        const caseIndexMatch = caseItem.id?.match(/case[_-](\d+)$/);
        const caseIndex = caseIndexMatch ? parseInt(caseIndexMatch[1], 10) + 1 : index + 1;
        
        // Check if case has evaluation object (new format) or flat structure (old format)
        const hasEvaluation = caseItem.evaluation && typeof caseItem.evaluation === 'object' && Object.keys(caseItem.evaluation).length > 0;
        
        if (hasEvaluation) {
          // New format: evaluation object already exists
          const evalData = caseItem.evaluation;
          return {
            case_index: caseIndex,
            case_id: caseItem.id || `case-${index + 1}`,
            coach_response: caseItem.coach_response || '',
            evaluation: {
              score: evalData.score ?? null,
              correctness: evalData.correctness ?? null,
              relevance: evalData.relevance ?? null,
              guidelines_adherence: evalData.guidelines_adherence ?? null,
              reasoning: evalData.reasoning || '',
              issues: evalData.issues || [],
            },
            case_metadata: {
              category: caseItem.category || (caseItem.case_metadata?.category) || (caseItem.metadata?.category) || '',
              expected_quality: caseItem.expected_quality || (caseItem.case_metadata?.expected_quality) || (caseItem.metadata?.expected_quality) || undefined,
            },
            integration_status: caseItem.integration_status || 'ok',
            history: caseItem.history || null, // Include conversation history if available
            ltm_context: caseItem.ltm_context || null, // Include LTM context if available
            judge_conversation_history: caseItem.judge_conversation_history || null, // Include formatted history sent to judge
            coach_conversation_summaries: caseItem.coach_conversation_summaries || null, // Include conversation summaries available to coach
            prompt_type: caseItem.prompt_type || null, // Include prompt type used by coach
            // Technical metadata - check multiple possible locations
            response_time_ms: caseItem.response_time_ms || caseItem.evaluation?.response_time_ms || caseItem.metadata?.response_time_ms || null,
            judge_model: caseItem.judge_model || caseItem.evaluation?.judge_model || caseItem.metadata?.judge_model || caseItem.evaluation?.modelUsed || null,
            evaluated_at: caseItem.evaluated_at || caseItem.evaluation?.evaluated_at || caseItem.metadata?.evaluated_at || caseItem.timestamp || null,
            responded_to_index: caseItem.responded_to_index || null,
          };
        } else if (caseItem.score_correctness !== undefined || caseItem.score_tone !== undefined) {
          // Old format: flat structure with score_correctness, score_tone, etc.
          const primaryScore = caseItem.score_correctness !== undefined && caseItem.score_correctness !== null
            ? (caseItem.score_tone !== undefined && caseItem.score_tone !== null
                ? (caseItem.score_correctness + caseItem.score_tone) / 2
                : caseItem.score_correctness)
            : (caseItem.score_tone !== undefined && caseItem.score_tone !== null
                ? caseItem.score_tone
                : 0.5);
          
          return {
            case_index: caseIndex,
            case_id: caseItem.case_id || caseItem.id || `case-${index + 1}`,
            coach_response: caseItem.coach_response || '',
            evaluation: {
              score: primaryScore,
              correctness: caseItem.score_correctness || 0,
              relevance: caseItem.score_tone || 0,
              guidelines_adherence: primaryScore,
              reasoning: caseItem.judge_reasoning || '',
              issues: caseItem.pass_fail === false ? ['Score below threshold'] : [],
            },
            case_metadata: {
              category: caseItem.category || (caseItem.case_metadata?.category) || (caseItem.metadata?.category) || '',
              expected_quality: caseItem.expected_quality || (caseItem.case_metadata?.expected_quality) || (caseItem.metadata?.expected_quality) || undefined,
            },
            integration_status: caseItem.integration_status || 'ok',
            history: caseItem.history || null, // Include conversation history if available
            ltm_context: caseItem.ltm_context || null, // Include LTM context if available
            judge_conversation_history: caseItem.judge_conversation_history || null, // Include formatted history sent to judge
            coach_conversation_summaries: caseItem.coach_conversation_summaries || null, // Include conversation summaries available to coach
            prompt_type: caseItem.prompt_type || null, // Include prompt type used by coach
            // Technical metadata - check multiple possible locations
            response_time_ms: caseItem.response_time_ms || caseItem.evaluation?.response_time_ms || caseItem.metadata?.response_time_ms || null,
            judge_model: caseItem.judge_model || caseItem.evaluation?.judge_model || caseItem.metadata?.judge_model || caseItem.evaluation?.modelUsed || null,
            evaluated_at: caseItem.evaluated_at || caseItem.evaluation?.evaluated_at || caseItem.metadata?.evaluated_at || caseItem.timestamp || null,
            responded_to_index: caseItem.responded_to_index || null,
          };
        } else {
          // No evaluation data available
          return {
            case_index: caseIndex,
            case_id: caseItem.id || `case-${index + 1}`,
            coach_response: caseItem.coach_response || '',
            evaluation: {
              score: null,
              correctness: null,
              relevance: null,
              guidelines_adherence: null,
              reasoning: 'No evaluation data available',
              issues: [],
            },
            case_metadata: {
              category: caseItem.category || (caseItem.case_metadata?.category) || (caseItem.metadata?.category) || '',
              expected_quality: caseItem.expected_quality || (caseItem.case_metadata?.expected_quality) || (caseItem.metadata?.expected_quality) || undefined,
            },
            integration_status: caseItem.integration_status || 'ok',
            history: caseItem.history || null, // Include conversation history if available
            ltm_context: caseItem.ltm_context || null, // Include LTM context if available
            judge_conversation_history: caseItem.judge_conversation_history || null, // Include formatted history sent to judge
            coach_conversation_summaries: caseItem.coach_conversation_summaries || null, // Include conversation summaries available to coach
            prompt_type: caseItem.prompt_type || null, // Include prompt type used by coach
            // Technical metadata - check multiple possible locations
            response_time_ms: caseItem.response_time_ms || caseItem.evaluation?.response_time_ms || caseItem.metadata?.response_time_ms || null,
            judge_model: caseItem.judge_model || caseItem.evaluation?.judge_model || caseItem.metadata?.judge_model || caseItem.evaluation?.modelUsed || null,
            evaluated_at: caseItem.evaluated_at || caseItem.evaluation?.evaluated_at || caseItem.metadata?.evaluated_at || caseItem.timestamp || null,
            responded_to_index: caseItem.responded_to_index || null,
          };
        }
      });
      
      // Calculate metrics
      const totalCases = transformedResults.length;
      const avgScore = totalCases > 0 
        ? transformedResults.reduce((sum, r) => sum + (r.evaluation?.score || 0), 0) / totalCases
        : 0;
      const minScore = transformedResults.length > 0
        ? Math.min(...transformedResults.map(r => r.evaluation?.score || 0))
        : 0;
      const maxScore = transformedResults.length > 0
        ? Math.max(...transformedResults.map(r => r.evaluation?.score || 0))
        : 0;
      
      // Group by category
      const byCategory = {};
      transformedResults.forEach(result => {
        const cat = result.case_metadata?.category || 'unknown';
        if (!byCategory[cat]) {
          byCategory[cat] = { count: 0, totalScore: 0 };
        }
        byCategory[cat].count++;
        byCategory[cat].totalScore += result.evaluation?.score || 0;
      });
      
      // Calculate avg_score per category
      Object.keys(byCategory).forEach(cat => {
        const catData = byCategory[cat];
        byCategory[cat] = {
          count: catData.count,
          avg_score: catData.count > 0 ? catData.totalScore / catData.count : 0,
        };
      });
      
      // Build score distribution
      const highCount = transformedResults.filter(r => (r.evaluation?.score || 0) >= 0.8).length;
      const mediumCount = transformedResults.filter(r => {
        const score = r.evaluation?.score || 0;
        return score >= 0.5 && score < 0.8;
      }).length;
      const lowCount = transformedResults.filter(r => (r.evaluation?.score || 0) < 0.5).length;
      
      // Set transformed data
      setEvaluationData({
        results: transformedResults,
        metrics: {
          overall: {
            total_cases: totalCases,
            avg_score: avgScore,
            min_score: minScore,
            max_score: maxScore,
          },
          score_distribution: {
            high: { 
              count: highCount, 
              percentage: totalCases > 0 ? (highCount / totalCases) * 100 : 0 
            },
            medium: { 
              count: mediumCount, 
              percentage: totalCases > 0 ? (mediumCount / totalCases) * 100 : 0 
            },
            low: { 
              count: lowCount, 
              percentage: totalCases > 0 ? (lowCount / totalCases) * 100 : 0 
            },
          },
          by_category: byCategory,
        },
        suite: {
          id: suite.id,
          name: suite.name,
          description: suite.description,
        }
      });
      
      setLastFetchedAt(new Date().toISOString());
    } catch (fetchError) {
      setError(fetchError.message || 'Unexpected error loading evaluation data.');
      console.error('Error loading evaluation data:', fetchError);
    } finally {
      setLoading(false);
    }
  }, [getToken, availableSuites]);

  const loadAvailableSuites = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${apiUrl}/api/evaluation/suites`, { headers });
      
      if (response.ok) {
        const suites = await response.json();
        // Ensure suites is always an array
        if (Array.isArray(suites) && suites.length > 0) {
          // Sort suites: Options: 1) Newest first (by createdAt), 2) Alphabetical (by name), 3) Most cases first
          // Currently using: Newest first (most recent evaluations first)
          const sortedSuites = [...suites].sort((a, b) => {
            // Option 1: Newest first (by creation date) - CURRENT
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA; // Descending (newest first)
            
            // Option 2: Alphabetical by name (uncomment to use)
            // const nameA = (a.name || a.id || '').toLowerCase();
            // const nameB = (b.name || b.id || '').toLowerCase();
            // return nameA.localeCompare(nameB);
            
            // Option 3: Most cases first (uncomment to use)
            // const casesA = a.caseCount || 0;
            // const casesB = b.caseCount || 0;
            // return casesB - casesA; // Descending (most cases first)
          });
          
          // Format suite names with human-readable identifiers (sequential number + timestamp + case count)
          // First, sort by date ascending (oldest first) to assign chronological numbers correctly
          const suitesForNumbering = [...suites].sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateA - dateB; // Ascending (oldest first) for numbering
          });
          
          // Create a map of suite ID to chronological number (oldest = #001)
          const suiteNumberMap = new Map();
          suitesForNumbering.forEach((suite, index) => {
            suiteNumberMap.set(suite.id, index + 1); // #001, #002, etc.
          });
          
          // Now format suites (still sorted newest first for display)
          const formattedSuites = sortedSuites.map((suite) => {
            const suiteDate = suite.createdAt ? new Date(suite.createdAt) : new Date();
            const dateStr = suiteDate.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
            const timeStr = suiteDate.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            });
            // Get chronological number from map (oldest = #001)
            const seqNum = suiteNumberMap.get(suite.id) || 1;
            const caseCount = suite.caseCount || 0;
            
            // Format: "#001 - Dec 7, 2025 16:09 (23 cases)" or use existing name if it's already formatted
            const formattedName = suite.name && suite.name.includes('#') 
              ? suite.name 
              : `#${String(seqNum).padStart(3, '0')} - ${dateStr} ${timeStr} (${caseCount} ${caseCount === 1 ? 'case' : 'cases'})`;
            
            return {
              ...suite,
              displayName: formattedName
            };
          });
          
          setAvailableSuites(formattedSuites);
          // Validate current selection and update if invalid or not set
          // Use functional update to avoid dependency on selectedSuiteId
          setSelectedSuiteId((currentId) => {
            const currentSuiteExists = currentId && sortedSuites.some(s => s.id === currentId);
            if (!currentSuiteExists) {
              // Update to first available suite if current is invalid or not set
              return sortedSuites[0].id;
            }
            return currentId; // Keep current selection if valid
          });
        } else {
          console.warn('API returned non-array response:', suites);
          // Don't set invalid default - let user see error
        }
      } else {
        // API failed - don't set invalid default
        console.warn('Failed to load suites from API');
      }
    } catch (err) {
      console.warn('Error loading suites from API:', err);
      // Don't set invalid default - let user see error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  useEffect(() => {
    if (isAuthorized) {
      loadAvailableSuites();
    } else if (isConfigured) {
      setError('You are not authorized to access this dashboard. Please contact an administrator.');
    }
  }, [isAuthorized, isConfigured, loadAvailableSuites]);

  // Load judge rubric when suite is selected
  const loadJudgeRubric = useCallback(async () => {
    setRubricLoading(true);
    try {
      const apiUrl = getApiUrl();
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${apiUrl}/api/evaluation/judge/rubric`, {
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to load rubric`);
      }
      
      const rubricData = await response.json();
      setJudgeRubric(rubricData);
    } catch (err) {
      console.error('Failed to load judge rubric:', err);
      setJudgeRubric(null);
    } finally {
      setRubricLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (selectedSuiteId) {
      loadEvaluationData(selectedSuiteId);
      loadJudgeRubric(); // Load rubric when suite is selected
    }
  }, [selectedSuiteId, loadEvaluationData, loadJudgeRubric]);

  const filteredResults = useMemo(() => {
    if (!evaluationData?.results) return [];
    let filtered = [...evaluationData.results];
    
    // Apply score filter (from score distribution bars)
    if (scoreFilter) {
      filtered = filtered.filter((result) => {
        const score = result.evaluation?.score ?? 0;
        if (scoreFilter === 'high') {
          return score >= 0.8;
        } else if (scoreFilter === 'medium') {
          return score >= 0.5 && score < 0.8;
        } else if (scoreFilter === 'low') {
          return score < 0.5;
        }
        return true;
      });
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((result) => {
        const response = result.coach_response?.toLowerCase() || '';
        const reasoning = result.evaluation?.reasoning?.toLowerCase() || '';
        const category = result.case_metadata?.category?.toLowerCase() || '';
        return response.includes(query) || reasoning.includes(query) || category.includes(query);
      });
    }
    if (filters.category) {
      filtered = filtered.filter((result) => result.case_metadata?.category === filters.category);
    }
    if (filters.expected_quality) {
      filtered = filtered.filter((result) => result.case_metadata?.expected_quality === filters.expected_quality);
    }
    filtered = filtered.filter((result) => {
      const score = result.evaluation?.score ?? 0;
      return score >= filters.minScore && score <= filters.maxScore;
    });
    filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'score':
          aValue = a.evaluation?.score ?? 0;
          bValue = b.evaluation?.score ?? 0;
          break;
        case 'category':
          aValue = a.case_metadata?.category || '';
          bValue = b.case_metadata?.category || '';
          break;
        default:
          aValue = a.case_index ?? 0;
          bValue = b.case_index ?? 0;
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [evaluationData, searchQuery, filters, sortConfig, scoreFilter]);

  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredResults.slice(start, start + pageSize);
  }, [filteredResults, currentPage, pageSize]);

  const analytics = useMemo(() => {
    if (!evaluationData?.metrics) {
      return {
        overall: { total_cases: 0, avg_score: 0, min_score: 0, max_score: 0, pass_rate: 0 },
        score_distribution: { high: { count: 0, percentage: 0 }, medium: { count: 0, percentage: 0 }, low: { count: 0, percentage: 0 } },
        by_category: {},
      };
    }
    const metrics = evaluationData.metrics;
    const overall = metrics.overall || {};
    const scoreDist = metrics.score_distribution || {};
    const byCategory = metrics.by_category || {};
    const totalResults = filteredResults.length;
    const passingResults = filteredResults.filter((r) => (r.evaluation?.score ?? 0) >= 0.8).length;
    const passRate = totalResults > 0 ? (passingResults / totalResults) * 100 : 0;
    return {
      overall: {
        total_cases: overall.total_cases || totalResults,
        avg_score: overall.avg_score || 0,
        min_score: overall.min_score || 0,
        max_score: overall.max_score || 0,
        pass_rate: passRate,
      },
      score_distribution: {
        high: scoreDist.high || { count: 0, percentage: 0 },
        medium: scoreDist.medium || { count: 0, percentage: 0 },
        low: scoreDist.low || { count: 0, percentage: 0 },
      },
      by_category: byCategory,
    };
  }, [evaluationData, filteredResults]);

  const handleFilterChange = (key, value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };
  
  const handleScoreFilterClick = (filterType) => {
    setCurrentPage(1);
    setScoreFilter(scoreFilter === filterType ? null : filterType);
    
    // Smooth scroll to Score Distribution section
    setTimeout(() => {
      const scoreDistSection = document.getElementById('score-distribution-section');
      if (scoreDistSection) {
        scoreDistSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure filter is applied first
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleExport = useCallback(
    (format) => {
      if (!filteredResults.length || typeof document === 'undefined') return;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      if (format === 'json') {
        const payload = JSON.stringify(
          { suite: evaluationData?.suite, results: filteredResults, metrics: analytics, exported_at: new Date().toISOString() },
          null,
          2
        );
        const blob = new Blob([payload], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `evaluation_export_${timestamp}.json`;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      // CSV export with UTF-8 BOM for proper encoding
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      const headers = ['Case Index', 'Category', 'Expected Quality', 'Score', 'Correctness', 'Relevance', 'Guidelines Adherence', 'Has Issues', 'Integration Status'];
      const rows = filteredResults.map((result) => [
        result.case_index || '',
        escapeCsv(result.case_metadata?.category || ''),
        escapeCsv(result.case_metadata?.expected_quality || ''),
        (result.evaluation?.score ?? 0).toFixed(3),
        (result.evaluation?.correctness ?? 0).toFixed(3),
        (result.evaluation?.relevance ?? 0).toFixed(3),
        (result.evaluation?.guidelines_adherence ?? 0).toFixed(3),
        (result.evaluation?.issues?.length > 0 ? 'Yes' : 'No'),
        escapeCsv(result.integration_status || ''),
      ]);
      const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((cells) => cells.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evaluation_export_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    [filteredResults, evaluationData, analytics]
  );

  const categories = useMemo(() => {
    if (!evaluationData?.results) return [];
    const cats = new Set();
    evaluationData.results.forEach((r) => {
      if (r.case_metadata?.category) cats.add(r.case_metadata.category);
    });
    return Array.from(cats).sort();
  }, [evaluationData]);

  const totalPages = Math.ceil(filteredResults.length / pageSize);
  
  // Reset to page 1 when page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // Admin access check - show error if not authorized
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-200 flex items-center justify-center px-6">
        <div className="max-w-xl space-y-4 text-center">
          <p className="text-2xl font-semibold text-white">Admin access not configured</p>
          <p className="text-gray-400">
            Set <code className="text-blue-300">REACT_APP_ADMIN_EMAIL_WHITELIST</code> to a comma-separated list of admin
            emails so the admin console can enforce access controls.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-200 flex items-center justify-center px-6">
        <div className="max-w-lg space-y-4 text-center">
          <p className="text-2xl font-semibold text-white">Restricted area</p>
          <p className="text-gray-400">
            Your account is not in the admin allowlist. If you believe this is an error, please contact the Runium team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="mx-auto w-full max-w-7xl px-6 py-10 space-y-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm uppercase tracking-widest text-blue-400">Admin</p>
                <h1 className="text-3xl font-semibold text-white">LLM Evaluation Dashboard</h1>
                <p className="text-sm text-gray-400">Monitor and analyze LLM-as-Judge evaluation results in real-time.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center rounded-lg border border-gray-700 bg-black/30 px-3 py-2 text-xs font-medium text-gray-300 hover:text-white hover:border-blue-400 hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back to Coach
              </button>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap mt-4">
              <div className="flex-1 min-w-[200px] max-w-[400px]">
                <label className="text-xs uppercase tracking-wide text-gray-500 block mb-1">Suite</label>
                <select
                  value={selectedSuiteId || ''}
                  onChange={(e) => setSelectedSuiteId(e.target.value)}
                  className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-400/70 hover:bg-blue-500/15 hover:shadow-[0_0_0_1px_rgba(96,165,250,0.3)] transition-all duration-200 cursor-pointer [&>option]:bg-gray-900 [&>option]:text-gray-100"
                  style={{
                    colorScheme: 'dark'
                  }}
                  disabled={loading || !Array.isArray(availableSuites) || availableSuites.length === 0}
                >
                  {!Array.isArray(availableSuites) || availableSuites.length === 0 ? (
                    <option value="" className="bg-gray-900 text-gray-100">{loading ? 'Loading suites...' : 'No suites available'}</option>
                  ) : (
                    availableSuites.map((suite) => (
                      <option key={suite.id} value={suite.id} className="bg-gray-900 text-gray-100">
                        {suite.displayName || suite.name || suite.id}
                      </option>
                    ))
                  )}
                </select>
              </div>
              
              {selectedSuiteId && availableSuites.length > 0 && (() => {
                const selectedSuite = availableSuites.find(s => s.id === selectedSuiteId);
                if (!selectedSuite) return null;
                
                // Format date with proper timezone handling
                const formatDate = (dateStr) => {
                  if (!dateStr) return null;
                  const date = new Date(dateStr);
                  // Check if date is valid
                  if (isNaN(date.getTime())) return null;
                  // Format as "Dec 8, 2025" (local timezone)
                  return date.toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  });
                };
                
                const formattedDate = formatDate(selectedSuite.createdAt);
                const suiteId = selectedSuite.id;
                const evalType = selectedSuite.evaluation_type;
                
                // Only show if we have at least one piece of info
                if (!formattedDate && !suiteId && !evalType) return null;
                
                return (
                  <div className="flex items-center gap-2 text-xs">
                    {formattedDate && (
                      <span className="inline-flex items-center rounded-md border border-gray-700/50 bg-gray-800/30 px-2 py-1 text-gray-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mr-1.5 h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formattedDate}
                      </span>
                    )}
                    {suiteId && (
                      <span className="inline-flex items-center rounded-md border border-gray-700/50 bg-gray-800/30 px-2 py-1 text-gray-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mr-1.5 h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {suiteId}
                      </span>
                    )}
                    {evalType && (
                      <span className="inline-flex items-center rounded-md border border-blue-700/50 bg-blue-900/20 px-2 py-1 text-blue-200">
                        <span className="capitalize">{evalType === 'real_coach' ? 'Production Coach' : evalType.replace(/_/g, ' ')}</span>
                        <span className="mx-1.5 text-blue-400 font-medium">vs.</span>
                        <span className="text-blue-300">GPT-4</span>
                      </span>
                    )}
                  </div>
                );
              })()}
              
              <div className="flex items-center gap-2 ml-auto">
                {lastFetchedAt && (
                  <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:inline">
                    Updated {new Date(lastFetchedAt).toLocaleTimeString(undefined, { timeStyle: 'short' })}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => loadEvaluationData(selectedSuiteId)}
                  className="inline-flex items-center rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-blue-200 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Judge Rubric Section */}
        {selectedSuiteId && (
          <div className="mb-4 rounded-2xl border border-gray-800 bg-gray-900/40 p-4">
            <button
              type="button"
              onClick={() => setShowRubric(!showRubric)}
              className="w-full flex items-center justify-between text-left"
            >
              <div>
                <p className="text-sm font-medium text-gray-200">
                  Evaluation Rubric
                  {judgeRubric?.version && (
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      (Version: {judgeRubric.version})
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  The criteria used by the LLM judge to evaluate coach responses
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${showRubric ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showRubric && (
              <div className="mt-4 pt-4 border-t border-gray-800">
                {rubricLoading ? (
                  <div className="text-center py-8 text-gray-400">Loading rubric...</div>
                ) : judgeRubric ? (
                  <div className="space-y-4">
                    {judgeRubric.description && (
                      <p className="text-sm text-gray-300">{judgeRubric.description}</p>
                    )}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <div className="whitespace-pre-wrap font-mono text-xs text-gray-300 max-h-96 overflow-y-auto">
                        {judgeRubric.template}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      This rubric defines how the judge evaluates coach responses for correctness, relevance, and guidelines adherence.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Unable to load rubric. Please try refreshing.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mb-4">
          <button
            type="button"
            onClick={() => {/* TODO: Implement multi-suite comparison */}}
            className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 opacity-50 cursor-not-allowed"
            disabled
            title="Coming soon: Compare multiple suites"
          >
            Compare Suites
          </button>
          <button
            type="button"
            onClick={() => handleExport('csv')}
            disabled={!filteredResults.length}
            className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport('json')}
            disabled={!filteredResults.length}
            className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Export JSON
          </button>
        </div>

        <section className="rounded-2xl border border-gray-900 bg-gray-900/60 p-3 sm:p-4 shadow-lg">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="text-xs uppercase tracking-wide text-gray-500">Search</label>
              <input
                type="text"
                placeholder="Search responses, reasoning, or category"
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Expected Quality</label>
              <select
                value={filters.expected_quality}
                onChange={(e) => handleFilterChange('expected_quality', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="high">High</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Min Score</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={filters.minScore}
                onChange={(e) => handleFilterChange('minScore', parseFloat(e.target.value) || 0)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </section>

        {!loading && evaluationData && (
          <section className="rounded-2xl border border-gray-900 bg-gray-900/50 p-5 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 pb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500">Metrics</p>
                <h2 className="text-lg font-semibold text-white">Overall Performance</h2>
              </div>
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-4">
              <MetricsCard label="Total Cases" value={analytics.overall.total_cases} subtitle={`${filteredResults.length} after filters`} />
              <MetricsCard
                label="Average Score"
                value={((analytics.overall.avg_score || 0) * 100).toFixed(1) + '%'}
                subtitle={`Range: ${((analytics.overall.min_score || 0) * 100).toFixed(1)}% - ${((analytics.overall.max_score || 0) * 100).toFixed(1)}%`}
              />
              <MetricsCard label="Pass Rate" value={(analytics.overall.pass_rate ?? 0).toFixed(1) + '%'} subtitle="Scores ≥ 80%" />
              <MetricsCard
                label="Success Rate"
                value={
                  analytics.overall.total_cases > 0
                    ? (((analytics.overall.total_cases - (evaluationData.metrics?.overall?.failed_cases || 0)) / analytics.overall.total_cases) * 100).toFixed(1) + '%'
                    : '0%'
                }
                subtitle="Successful evaluations"
              />
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div id="score-distribution-section">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Score Distribution</p>
                <div className="space-y-3">
                  <ScoreDistributionBar 
                    label="High (≥80%)" 
                    count={analytics.score_distribution.high.count} 
                    total={analytics.overall.total_cases} 
                    percentage={analytics.score_distribution.high.percentage}
                    onClick={() => handleScoreFilterClick('high')}
                    isActive={scoreFilter === 'high'}
                  />
                  <ScoreDistributionBar 
                    label="Medium (50-79%)" 
                    count={analytics.score_distribution.medium.count} 
                    total={analytics.overall.total_cases} 
                    percentage={analytics.score_distribution.medium.percentage}
                    onClick={() => handleScoreFilterClick('medium')}
                    isActive={scoreFilter === 'medium'}
                  />
                  <ScoreDistributionBar 
                    label="Low (<50%)" 
                    count={analytics.score_distribution.low.count} 
                    total={analytics.overall.total_cases} 
                    percentage={analytics.score_distribution.low.percentage}
                    onClick={() => handleScoreFilterClick('low')}
                    isActive={scoreFilter === 'low'}
                  />
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">By Category</p>
                <ul className="space-y-2 text-sm text-gray-200">
                  {Object.entries(analytics.by_category).length > 0 ? (
                    Object.entries(analytics.by_category).map(([category, data]) => (
                      <li key={category} className="flex items-center justify-between rounded-lg border border-gray-900 bg-black/20 px-3 py-2">
                        <span className="capitalize">{category.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{data.count}</span>
                          <span className="text-xs text-gray-400">({((data.avg_score || 0) * 100).toFixed(1)}%)</span>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500 text-xs">No category data available.</li>
                  )}
                </ul>
              </div>
            </div>
          </section>
        )}

        {error && <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-4 text-sm text-red-100">{error}</div>}
        {loading && <div className="rounded-xl border border-gray-900 bg-gray-900/40 p-10 text-center text-gray-400">Loading evaluation data...</div>}
        {!loading && !error && (!evaluationData || filteredResults.length === 0) && (
          <div className="rounded-xl border border-gray-900 bg-gray-900/40 p-10 text-center text-gray-500">
            {!evaluationData ? 'No evaluation data available.' : 'No results found for the selected filters.'}
          </div>
        )}

        {!loading && !error && filteredResults.length > 0 && (
          <section className="rounded-2xl border border-gray-900 bg-gray-900/40 shadow-xl">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-900 text-sm" role="table">
                <thead className="bg-gray-900/60">
                  <tr role="row" className="text-left text-xs uppercase tracking-widest text-gray-400">
                    <th role="columnheader" className="px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('case_index')}>
                      ID {sortConfig.key === 'case_index' && <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th role="columnheader" className="px-4 py-3 font-medium">Category</th>
                    <th role="columnheader" className="px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('score')}>
                      Score {sortConfig.key === 'score' && <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th role="columnheader" className="px-4 py-3 font-medium">Correctness</th>
                    <th role="columnheader" className="px-4 py-3 font-medium">Relevance</th>
                    <th role="columnheader" className="px-4 py-3 font-medium">Guidelines</th>
                    <th role="columnheader" className="px-4 py-3 font-medium">Response Preview</th>
                    <th role="columnheader" className="px-4 py-3 font-medium">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-900" role="rowgroup">
                  {paginatedResults.map((result, index) => {
                    const score = result.evaluation?.score ?? 0;
                    const responsePreview = result.coach_response?.substring(0, 100) || '';
                    const hasIssues = (result.evaluation?.issues?.length || 0) > 0;
                    
                    const setRowRef = (element) => {
                      if (element) {
                        rowRefs.current.set(index, element);
                      } else {
                        rowRefs.current.delete(index);
                      }
                    };
                    
                    const handleRowKeyDown = (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedResult(result);
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const nextIndex = index + 1;
                        if (nextIndex < paginatedResults.length) {
                          const nextRow = rowRefs.current.get(nextIndex);
                          if (nextRow) {
                            nextRow.focus();
                          }
                        }
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prevIndex = index - 1;
                        if (prevIndex >= 0) {
                          const prevRow = rowRefs.current.get(prevIndex);
                          if (prevRow) {
                            prevRow.focus();
                          }
                        }
                      }
                    };
                    
                    return (
                      <tr
                        key={result.case_index}
                        ref={setRowRef}
                        role="row"
                        tabIndex={0}
                        data-row-index={index}
                        className="bg-transparent transition hover:bg-blue-500/5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                        onClick={() => setSelectedResult(result)}
                        onKeyDown={handleRowKeyDown}
                        aria-label={`Case ${result.case_index}, Score ${(score * 100).toFixed(1)}%, Category ${result.case_metadata?.category || 'N/A'}`}
                      >
                        <td role="cell" className="px-4 py-3 font-mono text-xs text-gray-400">#{result.case_index}</td>
                        <td role="cell" className="px-4 py-3"><CategoryBadge category={result.case_metadata?.category} /></td>
                        <td role="cell" className="px-4 py-3"><ScoreBadge score={score} /></td>
                        <td role="cell" className="px-4 py-3 text-gray-300">
                          {result.evaluation?.correctness != null ? ((result.evaluation.correctness * 100).toFixed(1) + '%') : 'N/A'}
                        </td>
                        <td role="cell" className="px-4 py-3 text-gray-300">
                          {result.evaluation?.relevance != null ? ((result.evaluation.relevance * 100).toFixed(1) + '%') : 'N/A'}
                        </td>
                        <td role="cell" className="px-4 py-3 text-gray-300">
                          {result.evaluation?.guidelines_adherence != null ? ((result.evaluation.guidelines_adherence * 100).toFixed(1) + '%') : 'N/A'}
                        </td>
                        <td role="cell" className="px-4 py-3 text-white max-w-md">
                          <p className="truncate text-sm">{responsePreview}</p>
                          {result.evaluation?.reasoning && <p className="text-xs text-gray-500 mt-1 truncate">{result.evaluation.reasoning.substring(0, 80)}...</p>}
                        </td>
                        <td role="cell" className="px-4 py-3 text-center">
                          {hasIssues ? (
                            <span className="text-xs text-red-300">
                              {result.evaluation.issues.length} issue{result.evaluation.issues.length !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">None</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between border-t border-gray-900 px-4 py-3 text-xs text-gray-400 gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <p>Page {currentPage} of {totalPages} · {filteredResults.length} total results</p>
                <div className="flex items-center gap-2">
                  <label className="text-gray-500">Per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-lg border border-gray-800 bg-black/40 px-2 py-1 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-gray-900 [&>option]:text-gray-100"
                    style={{ colorScheme: 'dark' }}
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <option key={size} value={size} className="bg-gray-900 text-gray-100">
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="rounded-lg border border-gray-800 px-3 py-1 text-gray-300 disabled:cursor-not-allowed disabled:opacity-40 hover:border-blue-500 hover:text-blue-200 transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="rounded-lg border border-gray-800 px-3 py-1 text-gray-300 disabled:cursor-not-allowed disabled:opacity-40 hover:border-blue-500 hover:text-blue-200 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
      {selectedResult && (() => {
        const currentIndex = filteredResults.findIndex(r => r.case_index === selectedResult.case_index);
        const hasPrevious = currentIndex > 0;
        const hasNext = currentIndex < filteredResults.length - 1;
        
        const handlePrevious = () => {
          if (hasPrevious) {
            setSelectedResult(filteredResults[currentIndex - 1]);
          }
        };
        
        const handleNext = () => {
          if (hasNext) {
            setSelectedResult(filteredResults[currentIndex + 1]);
          }
        };
        
        return (
          <ErrorBoundary>
            <DetailModal 
              result={selectedResult} 
              onClose={() => setSelectedResult(null)}
              onPrevious={handlePrevious}
              onNext={handleNext}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
            />
          </ErrorBoundary>
        );
      })()}
    </div>
  );
};

export default EvaluationDashboard;

